import Field from "@/components/Field";
import Fieldset from "@/components/Fieldset";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import UserSelect from "@/components/UserSelect";
import { createEmptySubordinate } from "@/modules/structure-assignments/utils";
import { ContactRound, PlusIcon, XIcon } from "lucide-react";
import { memo, useCallback } from "react";
import { useFieldArray } from "react-hook-form";
import { useTranslation } from "react-i18next";

interface SubordinateFieldsetProps {
  role: string | string[] | undefined;
  subordinates?: Partial<{
    subordinate_id: string;
    subordinate_name: string;
  }>[];
}

export const SubordinatesFieldsets = memo(
  ({ role, subordinates }: SubordinateFieldsetProps) => {
    const { t } = useTranslation();

    const { fields, append, remove } = useFieldArray({
      name: "subordinates",
    });

    const onAddSubordinate = useCallback(() => {
      append(createEmptySubordinate());
    }, [append]);

    return (
      <TooltipProvider>
        {fields.map((field, index) => (
          <div key={field.id}>
            <Fieldset className="grid grid-cols-[min-content_1fr_3fr_30px] gap-6">
              <Field
                name={`subordinates.${index}.is_primary`}
                label={t("structureAssignments.fields.is_primary")}
                render={({ field }) => (
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                )}
              />
              <Field
                name={`subordinates.${index}.subordinate_id`}
                label={t("structureAssignments.fields.subordinate_id")}
                required={true}
                render={({ field }) => (
                  <UserSelect
                    systemRoleCodes={role}
                    {...field}
                    disabled={field.disabled || role === undefined}
                    className="min-w-5"
                    defaultItem={{
                      id: subordinates?.[index]?.subordinate_id,
                      name: subordinates?.[index]?.subordinate_name,
                    }}
                  />
                )}
              />

              <Field
                name={`subordinates.${index}.comment`}
                label={t("structureAssignments.fields.comment")}
                render={({ field }) => (
                  <textarea
                    {...field}
                    value={field.value || ""}
                    className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    placeholder={t("structureAssignments.placeholders.comment")}
                  />
                )}
              />
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    onClick={() => remove(index)}
                    variant="secondary"
                    className="w-7 h-7 mt-6"
                    data-testid={`remove-subordinate-${index}`}
                  >
                    <XIcon className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {t("structureAssignments.subordinates.remove")}
                </TooltipContent>
              </Tooltip>
            </Fieldset>
            <Separator />
          </div>
        ))}
        {fields.length == 0 && (
          <div className="text-sm text-muted-foreground flex flex-col justify-center items-center gap-3 mb-3">
            <ContactRound />
            {t("structureAssignments.subordinates.empty")}
          </div>
        )}
        <footer className="pt-5">
          <Button onClick={onAddSubordinate} variant="outline">
            <PlusIcon className="h-4 w-4 mr-2" />
            {t("structureAssignments.subordinates.add")}
          </Button>
        </footer>
      </TooltipProvider>
    );
  },
);
